import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { CalendarDays, Plus, Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { format, parseISO, isBefore, startOfToday } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = { date: '', endDate: '', name: '', description: '' };

const HolidayCalendar = () => {
  const { isAdmin } = useAuth();
  const admin = isAdmin();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${sessionStorage.getItem('auth_token')}` }
  });

  const fetchHolidays = async () => {
    try {
      const res = await axios.get(`${API}/holidays`, authHeaders());
      setHolidays(res.data);
    } catch (error) {
      console.error('Error loading holidays:', error);
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchHolidays(); }, []);

  const months = useMemo(() => {
    const groups = {};
    holidays
      .filter(h => h.date && h.date.startsWith(`${year}-`) && (monthFilter === 'all' || h.date.slice(5, 7) === monthFilter))
      .forEach(h => {
        const key = h.date.slice(0, 7);
        (groups[key] = groups[key] || []).push(h);
      });
    return Object.keys(groups).sort().map(key => ({
      key,
      label: format(parseISO(`${key}-01`), 'MMMM yyyy'),
      items: groups[key].sort((a, b) => a.date.localeCompare(b.date))
    }));
  }, [holidays, year, monthFilter]);

  const openAdd = () => {
    setEditing(null);
    setFormData({ ...emptyForm, date: `${year}-01-01` });
    setDialogOpen(true);
  };

  const openEdit = (h) => {
    setEditing(h);
    setFormData({ date: h.date, endDate: h.endDate || '', name: h.name, description: h.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.date || !formData.name.trim()) {
      toast.error('Date and holiday name are required');
      return;
    }
    if (formData.endDate && formData.endDate < formData.date) {
      toast.error('End date cannot be before the start date');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        date: formData.date,
        endDate: formData.endDate || null,
        name: formData.name.trim(),
        description: formData.description.trim() || null
      };
      if (editing) {
        await axios.put(`${API}/holidays/${editing.id}`, payload, authHeaders());
        toast.success('Holiday updated');
      } else {
        await axios.post(`${API}/holidays`, payload, authHeaders());
        toast.success('Holiday added');
      }
      setDialogOpen(false);
      fetchHolidays();
    } catch (error) {
      console.error('Error saving holiday:', error);
      toast.error(error.response?.data?.detail || 'Failed to save holiday');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (h) => {
    if (!window.confirm(`Delete "${h.name}"?`)) return;
    try {
      await axios.delete(`${API}/holidays/${h.id}`, authHeaders());
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (error) {
      console.error('Error deleting holiday:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete holiday');
    }
  };

  const today = startOfToday();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <CalendarDays className="h-7 w-7" />
            Holiday Calendar
          </h1>
          <p className="text-muted-foreground mt-1">Company holidays for the year</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setYear(year - 1)} data-testid="holiday-prev-year">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-lg font-semibold w-16 text-center">{year}</span>
          <Button size="sm" variant="outline" onClick={() => setYear(year + 1)} data-testid="holiday-next-year">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Select value={monthFilter} onValueChange={setMonthFilter}>
            <SelectTrigger className="w-40 h-9 bg-white" data-testid="holiday-month-filter">
              <SelectValue placeholder="All months" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All months</SelectItem>
              {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, idx) => (
                <SelectItem key={m} value={String(idx + 1).padStart(2, '0')}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {admin && (
            <Button size="sm" onClick={openAdd} className="ml-2" data-testid="add-holiday-btn">
              <Plus className="h-4 w-4 mr-1" />
              Add Holiday
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading holidays...</p>
      ) : months.length === 0 ? (
        <Card className="border-border/60 bg-white p-10 text-center text-muted-foreground">
          {monthFilter === 'all' ? `No holidays added for ${year} yet.` : 'No holidays in this month.'}
        </Card>
      ) : (
        months.map((month) => (
          <Card key={month.key} className="border-border/60 bg-white p-5">
            <h2 className="text-lg font-semibold mb-3">{month.label}</h2>
            <div className="divide-y">
              {month.items.map((h) => {
                const d = parseISO(h.date);
                const past = isBefore(h.endDate ? parseISO(h.endDate) : d, today);
                return (
                  <div key={h.id} className={`flex flex-wrap items-center gap-4 py-3 ${past ? 'opacity-60' : ''}`}>
                    <div className="min-w-[3.5rem] px-2 text-center rounded-lg bg-slate-100 py-1.5">
                      <div className="text-xl font-bold leading-none">{h.endDate && h.endDate !== h.date ? `${format(d, 'dd')}-${format(parseISO(h.endDate), 'dd')}` : format(d, 'dd')}</div>
                      <div className="text-xs uppercase text-muted-foreground mt-1">{format(d, 'MMM')}</div>
                    </div>
                    <div className="flex-1 min-w-[160px]">
                      <p className="font-semibold">{h.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {h.endDate && h.endDate !== h.date ? `${format(d, 'EEE, dd MMM')} – ${format(parseISO(h.endDate), 'EEE, dd MMM')}` : format(d, 'EEEE')}
                        {h.description ? ` • ${h.description}` : ''}
                      </p>
                    </div>
                    {admin && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(h)} data-testid={`edit-holiday-${h.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(h)} className="text-red-600" data-testid={`delete-holiday-${h.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        ))
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold">Start Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="mt-1.5 bg-white"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">End Date (optional)</Label>
                <Input
                  type="date"
                  value={formData.endDate}
                  min={formData.date || undefined}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="mt-1.5 bg-white"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">Leave End Date empty for a single-day holiday.</p>
            <div>
              <Label className="text-sm font-semibold">Holiday Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1.5 bg-white"
                placeholder="e.g., Diwali"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Note (optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="mt-1.5 bg-white"
                placeholder="e.g., Office closed"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Update Holiday' : 'Add Holiday'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HolidayCalendar;
